/**
 * Fait : le FILTRE du flux de génération pour un écran de fil. Le canal
 *   porte le projet ENTIER ; chaque message dit son fil et sa branche, et
 *   l'écran ne doit afficher QUE ceux de la branche qu'il montre. Sans ce
 *   filtre, la réponse d'un autre fil s'affichait dans le fil ouvert et le
 *   composer gelait sur toute génération du projet — deux membres dans deux
 *   fils, le scénario nominal de D-011, était contredit (bloquant du
 *   4e audit de la phase 5).
 *
 * Ne fait pas : il n'ouvre aucune connexion (`canal-du-fil.ts` s'en charge)
 *   et ne touche pas l'état de l'écran — il rend une écoute filtrée que
 *   `PageFil` branche sur ses propres gestes.
 */

import type { AttributionDuFlux, ÉcouteDuFil } from './canal-du-fil.ts';
import type { GenerationVisible } from './MessagesDuFil.tsx';

/** Ce que l'écran donne au filtre : où il est, et ce qu'il sait faire. */
export interface EcranDuFil {
  readonly filId: string;

  /** La branche montrée À L'INSTANT du message — jamais figée à l'ouverture. */
  brancheAffichee(): string | null;

  poseLaGeneration(mutation: (avant: GenerationVisible | null) => GenerationVisible | null): void;
  rechargeLaBranche(brancheId: string): void;
  montreLEchec(raison: string): void;
  surCoupure(): void;
}

/** Construit l'écoute du canal qui ne laisse passer que la branche montrée. */
export function ecouteDuFluxAffiche(ecran: EcranDuFil): ÉcouteDuFil {
  const concerne = (flux: AttributionDuFlux): boolean =>
    flux.conversationId === ecran.filId && flux.brancheId === ecran.brancheAffichee();

  return {
    surDebut: (generationId, flux) => {
      if (!concerne(flux)) return;
      ecran.poseLaGeneration(() => ({ generationId, brancheId: flux.brancheId, texte: '' }));
    },

    surBloc: (texte, generationId, flux) => {
      if (!concerne(flux)) return;

      // Un écran arrivé EN COURS de flux n'a pas vu le début : le premier
      // bloc de sa branche crée l'état, les suivants s'y ajoutent.
      ecran.poseLaGeneration((avant) =>
        avant === null || avant.generationId !== generationId
          ? { generationId, brancheId: flux.brancheId, texte }
          : { ...avant, texte: avant.texte + texte },
      );
    },

    surFin: (flux) => {
      if (!concerne(flux)) return;
      ecran.poseLaGeneration(() => null);
      ecran.rechargeLaBranche(flux.brancheId);
    },

    surAnnulation: (raison, flux) => {
      if (!concerne(flux)) return;
      ecran.poseLaGeneration(() => null);
      ecran.montreLEchec(raison);
    },

    surCoupure: () => {
      ecran.surCoupure();
    },
  };
}
